//src/utils/onboardingLifecycle.js
// src/utils/onboardingLifecycle.js

import { TEMPLATE_REGISTRY } from './templateRegistry';

/**
 * UTM Smart Manager onboarding lifecycle contract.
 *
 * Purpose:
 * - Keep onboarding state metadata-driven and vendor-neutral.
 * - Keep Firestore as the lifecycle source of truth.
 * - Let Loops, future Mixpanel, and manual concierge workflows consume the
 *   same normalized lifecycle fields without hardcoding template-specific logic.
 *
 * Enterprise rules:
 * - This file is frontend-safe only.
 * - Do NOT import Firebase, Loops, Mixpanel, React hooks, or browser routing here.
 * - Do NOT infer lifecycle intent by string parsing page copy or source labels.
 * - Template metadata should come from TEMPLATE_REGISTRY.
 * - Source attribution should come from SIGNUP_SOURCE_REGISTRY only.
 * - Unknown/general signups should stay unknown until an explicit source mapping exists.
 *
 * Backend note:
 * - Firebase Functions should use a backend-safe mirror of these constants/helpers
 *   rather than importing from /src.
 */

export const ONBOARDING_SCHEMA_VERSION = 1;

/**
 * Signup type describes how the account entered the product.
 *
 * selfServeTrial:
 * - User created a new trial account.
 * - This is the v1 onboarding focus.
 *
 * invitedUser:
 * - User joined an existing organization through an invite.
 * - Invited users should not enter the self-serve trial onboarding loop by default.
 */
export const SIGNUP_TYPES = Object.freeze({
  SELF_SERVE_TRIAL: 'selfServeTrial',
  INVITED_USER: 'invitedUser',
});

/**
 * Signup intent describes what the user was trying to do at signup time.
 *
 * general_signup:
 * - User signed up from a general website/app CTA.
 * - They likely need orientation before choosing Adobe, Google, or a workflow path.
 *
 * duplicate_template:
 * - User signed up to personalize a public template/free builder.
 * - Template metadata should determine likelyIntent and template context.
 */
export const SIGNUP_INTENTS = Object.freeze({
  GENERAL_SIGNUP: 'general_signup',
  DUPLICATE_TEMPLATE: 'duplicate_template',
});

/**
 * likelyIntent is intentionally broad.
 *
 * It should represent the workflow/search-intent family, not every landing page.
 * For v1, Adobe and Google are the entry wedges. Campaign URL Studio and Assets
 * Studio can be captured as broader workflow/product intent when explicitly known.
 */
export const LIKELY_INTENTS = Object.freeze({
  ADOBE: 'adobe',
  GOOGLE: 'google',
  CAMPAIGN_URL_STUDIO: 'campaignUrlStudio',
  ASSETS_STUDIO: 'assetsStudio',
  UNKNOWN: 'unknown',
});

/**
 * v1 user states.
 *
 * Keep these intentionally small.
 * High-intent/concierge is a flag, not a separate userState.
 */
export const USER_STATES = Object.freeze({
  GENERAL_WEBSITE_SIGNUP: 'generalWebsiteSignup',
  TEMPLATE_FREE_BUILDER_SIGNUP: 'templateFreeBuilderSignup',
  DORMANT_TRIAL_USER: 'dormantTrialUser',
});

/**
 * Activation statuses.
 *
 * Public template personalization can prove progress before final activation.
 * The v1 activation milestone is still:
 * - user generates at least one valid campaign URL submission.
 */
export const ACTIVATION_STATUSES = Object.freeze({
  NOT_STARTED: 'notStarted',
  TEMPLATE_OPENED: 'templateOpened',
  DRAFT_SUBMISSION_CREATED: 'draftSubmissionCreated',
  PUBLISHED_SUBMISSION_CREATED: 'publishedSubmissionCreated',
  ACTIVATED: 'activated',
});

/**
 * Lifecycle stage is broader than activationStatus.
 *
 * Example:
 * - activationStatus can be activated
 * - lifecycleStage can later become conversion_ready after publish
 */
export const LIFECYCLE_STAGES = Object.freeze({
  TRIAL_STARTED: 'trial_started',
  TEMPLATE_OPENED: 'template_opened',
  ACTIVATED: 'activated',
  CONVERSION_READY: 'conversion_ready',
  DORMANT_TRIAL: 'dormant_trial',
});

/**
 * Concierge status supports founder-led manual onboarding without requiring
 * a CRM or full automation engine in v1.
 */
export const CONCIERGE_STATUSES = Object.freeze({
  NOT_OFFERED: 'notOffered',
  OFFERED: 'offered',
  REPLIED: 'replied',
  HELPED: 'helped',
  NOT_FIT: 'notFit',
});

/**
 * Canonical onboarding events.
 *
 * These event names are intentionally vendor-neutral.
 * They can feed:
 * - Firestore onboarding state
 * - Loops events
 * - future Mixpanel analytics
 * - manual operating views
 */
export const ONBOARDING_EVENTS = Object.freeze({
  TRIAL_STARTED: 'trial_started',
  TEMPLATE_OPENED: 'template_opened',
  CAMPAIGN_URL_SUBMISSION_CREATED: 'campaign_url_submission_created',
  CAMPAIGN_URL_FORM_SAVED: 'campaign_url_form_saved',
  CAMPAIGN_URL_FORM_PUBLISHED: 'campaign_url_form_published',
  TRIAL_ENDING_SOON: 'trial_ending_soon',
  CONCIERGE_CANDIDATE_MARKED: 'concierge_candidate_marked',
  CONCIERGE_STATUS_UPDATED: 'concierge_status_updated',
  DORMANT_TRIAL_DETECTED: 'dormant_trial_detected',
});

/**
 * Submission modes used by activation event metadata.
 *
 * draft_or_test:
 * - Builder/test/draft submission.
 * - Important because users can experience first value before publishing.
 *
 * published:
 * - Published form submission.
 */
export const SUBMISSION_MODES = Object.freeze({
  DRAFT_OR_TEST: 'draft_or_test',
  PUBLISHED: 'published',
  UNKNOWN: 'unknown',
});

/**
 * Event source labels.
 *
 * These are stable internal names for audit/debugging.
 * They should not be shown to end users.
 */
export const ONBOARDING_EVENT_SOURCES = Object.freeze({
  SIGNUP_LOADING_PAGE: 'SignupLoadingPage',
  FORM_SUBMISSIONS_CONTEXT: 'FormSubmissionsContext',
  FORM_SAVE_CONTEXT: 'FormSaveContext',
  FORM_PUBLISH_CONTEXT: 'FormPublishContext',
  MANUAL_CONCIERGE: 'ManualConcierge',
  SCHEDULED_DETECTOR: 'ScheduledDetector',
  UNKNOWN: 'unknown',
});

/**
 * Source types classify where a signup source came from.
 *
 * This is not the same as likelyIntent.
 * Example:
 * - sourceType = freeBuilder
 * - likelyIntent = adobe
 */
export const SIGNUP_SOURCE_TYPES = Object.freeze({
  FREE_BUILDER: 'freeBuilder',
  SOLUTION_PAGE: 'solutionPage',
  PRODUCT_PAGE: 'productPage',
  HOMEPAGE: 'homepage',
  TEMPLATE_LIBRARY: 'templateLibrary',
  PRICING: 'pricing',
  UNKNOWN: 'unknown',
});

/**
 * Explicit signup source registry.
 *
 * Enterprise rule:
 * - Only add source values that are intentionally passed in signup URLs.
 * - Do not infer intent from arbitrary strings.
 * - templateKey metadata wins over source metadata.
 *
 * Current observed source values:
 * - adobe_landing_nav
 * - google_landing_nav
 *
 * Backward-compatible aliases:
 * - adobe_tool
 * - google_tool
 *
 * If website CTAs later add explicit source params such as:
 * - source=campaign_url_studio_hero
 * - source=assets_studio_hero
 * - source=homepage_header
 * add them here intentionally.
 */
export const SIGNUP_SOURCE_REGISTRY = Object.freeze({
  adobe_landing_nav: Object.freeze({
    label: 'Adobe free-builder personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  adobe_utm_builder_microsite_home: Object.freeze({
    label: 'Adobe UTM Builder microsite homepage personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  adobe_utm_builder_microsite_cid_builder: Object.freeze({
    label: 'Adobe CID Builder microsite personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  adobe_utm_builder_microsite_ccid_builder: Object.freeze({
    label: 'Adobe CCID Builder microsite personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  adobe_utm_builder_microsite_bulk_builder: Object.freeze({
    label: 'Bulk Adobe URL Builder microsite personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  google_landing_nav: Object.freeze({
    label: 'Google free-builder personalization CTA',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.GOOGLE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  adobe_tool: Object.freeze({
    label: 'Adobe free-builder personalization CTA legacy alias',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.ADOBE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),

  google_tool: Object.freeze({
    label: 'Google free-builder personalization CTA legacy alias',
    sourceType: SIGNUP_SOURCE_TYPES.FREE_BUILDER,
    likelyIntent: LIKELY_INTENTS.GOOGLE,
    defaultSignupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
    defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
    conciergeCandidateDefault: true,
  }),
});

/**
 * Centralized property-name list.
 *
 * This helps prevent drift when mapping:
 * - Firestore accounts/{uid}.onboarding
 * - Loops contact properties
 * - future Mixpanel user/profile properties
 * - manual operator views
 */
export const ONBOARDING_PROPERTY_NAMES = Object.freeze({
  SCHEMA_VERSION: 'schemaVersion',

  SIGNUP_TYPE: 'signupType',
  SIGNUP_INTENT: 'signupIntent',
  SIGNUP_SOURCE: 'signupSource',
  SIGNUP_SOURCE_TYPE: 'signupSourceType',
  SIGNUP_SOURCE_LABEL: 'signupSourceLabel',
  LIKELY_INTENT: 'likelyIntent',

  USER_STATE: 'userState',
  ACTIVATION_STATUS: 'activationStatus',
  LIFECYCLE_STAGE: 'lifecycleStage',

  TEMPLATE_KEY: 'templateKey',
  TEMPLATE_LABEL: 'templateLabel',
  TEMPLATE_FAMILY: 'templateFamily',
  TEMPLATE_OUTPUT_MODE: 'templateOutputMode',
  TEMPLATE_USE_CASE: 'templateUseCase',
  SOURCE_PUBLISHED_FORM_ID: 'sourcePublishedFormId',
  DUPLICATED_FORM_ID: 'duplicatedFormId',

  CONCIERGE_CANDIDATE: 'conciergeCandidate',
  CONCIERGE_STATUS: 'conciergeStatus',
  LAST_MANUAL_TOUCH_DATE: 'lastManualTouchDate',

  TRIAL_STARTED_AT: 'trialStartedAt',
  TRIAL_ENDS_AT: 'trialEndsAt',
  SUBSCRIPTION_STATUS: 'subscriptionStatus',
  PLAN_KEY: 'planKey',

  TEMPLATE_OPENED_AT: 'templateOpenedAt',
  FIRST_SUBMISSION_AT: 'firstSubmissionAt',
  FIRST_DRAFT_SUBMISSION_AT: 'firstDraftSubmissionAt',
  FIRST_PUBLISHED_SUBMISSION_AT: 'firstPublishedSubmissionAt',
  FIRST_SAVED_AT: 'firstSavedAt',
  FIRST_PUBLISHED_AT: 'firstPublishedAt',
  ACTIVATED_AT: 'activatedAt',
  LAST_ONBOARDING_EVENT_AT: 'lastOnboardingEventAt',

  LAST_LOOPS_SYNC_AT: 'lastLoopsSyncAt',
  LAST_LOOPS_EVENT_NAME: 'lastLoopsEventName',
  LAST_LOOPS_SYNC_STATUS: 'lastLoopsSyncStatus',

  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
});

/**
 * Destination names for future sync status objects.
 *
 * Loops is current v1 destination.
 * Mixpanel is intentionally disabled until future analytics implementation.
 */
export const LIFECYCLE_DESTINATIONS = Object.freeze({
  LOOPS: 'loops',
  MIXPANEL: 'mixpanel',
});

/**
 * Destination statuses for event audit trails.
 */
export const DESTINATION_SYNC_STATUSES = Object.freeze({
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
  SKIPPED: 'skipped',
  DISABLED: 'disabled',
});

/**
 * Utility: return enum values safely.
 */
const enumValues = (enumObject) => Object.values(enumObject || {});

/**
 * Utility: detect non-empty strings.
 */
const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Utility: clean string values without guessing semantic meaning.
 */
const cleanString = (value, fallback = '') => {
  if (!isNonEmptyString(value)) return fallback;
  return value.trim();
};

/**
 * Utility: normalize an enum value against an approved enum object.
 */
const normalizeEnumValue = (value, enumObject, fallback) => {
  const cleaned = cleanString(value, '');
  return enumValues(enumObject).includes(cleaned) ? cleaned : fallback;
};

/**
 * Utility: normalize booleans without coercing strings like "true".
 *
 * Keep this strict so external payloads cannot accidentally change lifecycle
 * flags through stringy form values.
 */
const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  return fallback;
};

/**
 * Utility: generate timestamp.
 *
 * timestampFactory can be:
 * - () => new Date().toISOString()
 * - () => serverTimestamp()
 *
 * This file intentionally does not import Firebase.
 */
const makeTimestamp = (timestampFactory = null) => {
  if (typeof timestampFactory === 'function') {
    try {
      return timestampFactory();
    } catch (error) {
      console.warn(
        '[onboardingLifecycle] timestampFactory failed; falling back to ISO timestamp:',
        error?.message || error
      );
    }
  }

  return new Date().toISOString();
};

/**
 * Utility: set a first-time timestamp only if it does not already exist.
 */
const setFirstTimestamp = (target, key, value) => {
  if (!target || !key) return target;
  if (target[key]) return target;

  return {
    ...target,
    [key]: value,
  };
};

/**
 * Utility: do not downgrade activated users back to a lower activation status.
 */
const preserveActivatedStatus = (
  currentActivationStatus,
  nextActivationStatus
) => {
  if (currentActivationStatus === ACTIVATION_STATUSES.ACTIVATED) {
    return ACTIVATION_STATUSES.ACTIVATED;
  }

  return nextActivationStatus;
};

/**
 * Utility: do not downgrade activated users back to earlier lifecycle stages
 * unless a later explicit stage is stronger.
 */
const preserveActivatedLifecycleStage = (
  currentActivationStatus,
  currentLifecycleStage,
  nextLifecycleStage
) => {
  if (
    currentActivationStatus === ACTIVATION_STATUSES.ACTIVATED &&
    currentLifecycleStage === LIFECYCLE_STAGES.CONVERSION_READY
  ) {
    return LIFECYCLE_STAGES.CONVERSION_READY;
  }

  if (
    currentActivationStatus === ACTIVATION_STATUSES.ACTIVATED &&
    nextLifecycleStage !== LIFECYCLE_STAGES.CONVERSION_READY
  ) {
    return LIFECYCLE_STAGES.ACTIVATED;
  }

  return nextLifecycleStage;
};

/**
 * Return true when onboarding state already exists.
 *
 * trial_started is an initialization event. It should create onboarding state
 * when no state exists, but it should not reset a user who has already opened
 * a template, activated, published, or become dormant.
 */
const hasExistingOnboardingState = (onboarding = {}) => {
  return Boolean(
    onboarding.schemaVersion ||
      onboarding.createdAt ||
      onboarding.lastOnboardingEventAt ||
      onboarding.userState ||
      onboarding.activationStatus ||
      onboarding.lifecycleStage
  );
};

/**
 * Merge a trial_started event into existing onboarding state.
 *
 * This keeps the event audit trail accurate while preventing lifecycle
 * downgrades from retries, delayed calls, manual tests, or out-of-order
 * trial_started events.
 */
const mergeTrialStartedIntoExistingState = ({
  currentOnboarding = {},
  initialState = {},
  now,
} = {}) => {
  return {
    ...initialState,
    ...currentOnboarding,

    trialStartedAt:
      currentOnboarding.trialStartedAt ||
      initialState.trialStartedAt ||
      now,
    trialEndsAt:
      currentOnboarding.trialEndsAt ||
      initialState.trialEndsAt ||
      null,
    subscriptionStatus:
      currentOnboarding.subscriptionStatus ||
      initialState.subscriptionStatus ||
      null,
    planKey:
      currentOnboarding.planKey ||
      initialState.planKey ||
      null,

    createdAt:
      currentOnboarding.createdAt ||
      initialState.createdAt ||
      now,
    updatedAt: now,
    lastOnboardingEventAt: now,
  };
};

/**
 * Public helper: validate onboarding event names.
 */
export const isValidOnboardingEventName = (eventName) =>
  enumValues(ONBOARDING_EVENTS).includes(eventName);

/**
 * Public helper: assert onboarding event names.
 */
export const assertValidOnboardingEventName = (eventName) => {
  if (!isValidOnboardingEventName(eventName)) {
    throw new Error(
      `[onboardingLifecycle] Invalid onboarding eventName: ${eventName}`
    );
  }

  return eventName;
};

export const normalizeSignupType = (signupType) =>
  normalizeEnumValue(
    signupType,
    SIGNUP_TYPES,
    SIGNUP_TYPES.SELF_SERVE_TRIAL
  );

export const normalizeSignupIntent = (signupIntent) =>
  normalizeEnumValue(
    signupIntent,
    SIGNUP_INTENTS,
    SIGNUP_INTENTS.GENERAL_SIGNUP
  );

export const normalizeLikelyIntent = (likelyIntent) =>
  normalizeEnumValue(
    likelyIntent,
    LIKELY_INTENTS,
    LIKELY_INTENTS.UNKNOWN
  );

export const normalizeUserState = (userState) =>
  normalizeEnumValue(
    userState,
    USER_STATES,
    USER_STATES.GENERAL_WEBSITE_SIGNUP
  );

export const normalizeActivationStatus = (activationStatus) =>
  normalizeEnumValue(
    activationStatus,
    ACTIVATION_STATUSES,
    ACTIVATION_STATUSES.NOT_STARTED
  );

export const normalizeLifecycleStage = (lifecycleStage) =>
  normalizeEnumValue(
    lifecycleStage,
    LIFECYCLE_STAGES,
    LIFECYCLE_STAGES.TRIAL_STARTED
  );

export const normalizeConciergeStatus = (conciergeStatus) =>
  normalizeEnumValue(
    conciergeStatus,
    CONCIERGE_STATUSES,
    CONCIERGE_STATUSES.NOT_OFFERED
  );

export const normalizeSubmissionMode = (submissionMode) =>
  normalizeEnumValue(
    submissionMode,
    SUBMISSION_MODES,
    SUBMISSION_MODES.UNKNOWN
  );

export const normalizeSignupSourceType = (sourceType) =>
  normalizeEnumValue(
    sourceType,
    SIGNUP_SOURCE_TYPES,
    SIGNUP_SOURCE_TYPES.UNKNOWN
  );

/**
 * Returns explicit metadata for a signup source.
 *
 * Enterprise rule:
 * - Source metadata must be explicitly registered.
 * - This function does not guess from strings.
 */
export const getSignupSourceMetadata = (
  signupSource,
  sourceRegistry = SIGNUP_SOURCE_REGISTRY
) => {
  const normalizedSource = cleanString(signupSource, '');

  if (!normalizedSource) {
    return {
      source: 'unknown',
      label: null,
      sourceType: SIGNUP_SOURCE_TYPES.UNKNOWN,
      likelyIntent: LIKELY_INTENTS.UNKNOWN,
      defaultSignupIntent: SIGNUP_INTENTS.GENERAL_SIGNUP,
      defaultUserState: USER_STATES.GENERAL_WEBSITE_SIGNUP,
      conciergeCandidateDefault: false,
      exists: false,
    };
  }

  const sourceEntry = sourceRegistry?.[normalizedSource] || null;

  if (!sourceEntry) {
    return {
      source: normalizedSource,
      label: null,
      sourceType: SIGNUP_SOURCE_TYPES.UNKNOWN,
      likelyIntent: LIKELY_INTENTS.UNKNOWN,
      defaultSignupIntent: SIGNUP_INTENTS.GENERAL_SIGNUP,
      defaultUserState: USER_STATES.GENERAL_WEBSITE_SIGNUP,
      conciergeCandidateDefault: false,
      exists: false,
    };
  }

  return {
    source: normalizedSource,
    label: sourceEntry.label || null,
    sourceType: normalizeSignupSourceType(sourceEntry.sourceType),
    likelyIntent: normalizeLikelyIntent(sourceEntry.likelyIntent),
    defaultSignupIntent: normalizeSignupIntent(
      sourceEntry.defaultSignupIntent
    ),
    defaultUserState: normalizeUserState(sourceEntry.defaultUserState),
    conciergeCandidateDefault: normalizeBoolean(
      sourceEntry.conciergeCandidateDefault,
      false
    ),
    exists: true,
  };
};

/**
 * Returns a template registry entry.
 */
export const getTemplateRegistryEntry = (
  templateKey,
  registry = TEMPLATE_REGISTRY
) => {
  const normalizedTemplateKey = cleanString(templateKey, '');

  if (!normalizedTemplateKey) return null;

  return registry?.[normalizedTemplateKey] || null;
};

/**
 * Returns raw lifecycle metadata for a template.
 */
export const getTemplateLifecycleMetadata = (
  templateKey,
  registry = TEMPLATE_REGISTRY
) => {
  const templateEntry = getTemplateRegistryEntry(templateKey, registry);
  return templateEntry?.lifecycle || null;
};

/**
 * Derives likelyIntent from a template entry.
 *
 * Template metadata is the highest-confidence intent source.
 */
export const deriveLikelyIntentFromTemplateEntry = (templateEntry) => {
  const lifecycle = templateEntry?.lifecycle || null;

  return normalizeLikelyIntent(
    lifecycle?.likelyIntent || LIKELY_INTENTS.UNKNOWN
  );
};

/**
 * Derives likelyIntent from an explicit source registry entry.
 *
 * This is intentionally explicit-only.
 */
export const deriveLikelyIntentFromSignupSource = (
  signupSource,
  sourceRegistry = SIGNUP_SOURCE_REGISTRY
) => {
  const sourceMetadata = getSignupSourceMetadata(signupSource, sourceRegistry);
  return normalizeLikelyIntent(sourceMetadata.likelyIntent);
};

/**
 * Derives likelyIntent.
 *
 * Priority:
 * 1. TEMPLATE_REGISTRY lifecycle metadata
 * 2. SIGNUP_SOURCE_REGISTRY explicit metadata
 * 3. provided fallback
 * 4. unknown
 *
 * No arbitrary string matching.
 */
export const deriveLikelyIntent = ({
  templateKey = null,
  templateEntry = null,
  signupSource = '',
  fallback = LIKELY_INTENTS.UNKNOWN,
  registry = TEMPLATE_REGISTRY,
  sourceRegistry = SIGNUP_SOURCE_REGISTRY,
} = {}) => {
  const resolvedTemplateEntry =
    templateEntry || getTemplateRegistryEntry(templateKey, registry);

  const templateLikelyIntent =
    deriveLikelyIntentFromTemplateEntry(resolvedTemplateEntry);

  if (templateLikelyIntent !== LIKELY_INTENTS.UNKNOWN) {
    return templateLikelyIntent;
  }

  const sourceLikelyIntent = deriveLikelyIntentFromSignupSource(
    signupSource,
    sourceRegistry
  );

  if (sourceLikelyIntent !== LIKELY_INTENTS.UNKNOWN) {
    return sourceLikelyIntent;
  }

  return normalizeLikelyIntent(fallback);
};

/**
 * Resolves flat template metadata from TEMPLATE_REGISTRY.
 *
 * This lets SignupLoadingPage and future backend functions use the same
 * normalized object without branching on individual template keys.
 */
export const resolveTemplateMetadata = ({
  templateKey = null,
  templateEntry = null,
  registry = TEMPLATE_REGISTRY,
} = {}) => {
  const normalizedTemplateKey = cleanString(templateKey, '');
  const resolvedTemplateEntry =
    templateEntry || getTemplateRegistryEntry(normalizedTemplateKey, registry);

  if (!resolvedTemplateEntry) {
    return {
      templateKey: normalizedTemplateKey || null,
      templateLabel: null,
      templateFamily: null,
      templateOutputMode: null,
      templateUseCase: null,
      sourcePublishedFormId: null,
      likelyIntent: LIKELY_INTENTS.UNKNOWN,
      defaultUserState: USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP,
      defaultActivationStatus: ACTIVATION_STATUSES.TEMPLATE_OPENED,
      conciergeCandidateDefault: false,
      exists: false,
    };
  }

  const lifecycle = resolvedTemplateEntry.lifecycle || {};

  return {
    templateKey: normalizedTemplateKey || null,
    templateLabel: resolvedTemplateEntry.label || null,
    templateFamily: lifecycle.templateFamily || null,
    templateOutputMode: lifecycle.templateOutputMode || null,
    templateUseCase: lifecycle.templateUseCase || null,
    sourcePublishedFormId: resolvedTemplateEntry.publishedFormId || null,
    likelyIntent: normalizeLikelyIntent(
      lifecycle.likelyIntent || LIKELY_INTENTS.UNKNOWN
    ),
    defaultUserState: normalizeUserState(
      lifecycle.defaultUserState ||
        USER_STATES.TEMPLATE_FREE_BUILDER_SIGNUP
    ),
    defaultActivationStatus: normalizeActivationStatus(
      lifecycle.defaultActivationStatus ||
        ACTIVATION_STATUSES.TEMPLATE_OPENED
    ),
    conciergeCandidateDefault: normalizeBoolean(
      lifecycle.conciergeCandidateDefault,
      false
    ),
    exists: true,
  };
};

/**
 * Builds the default destination status object for event audit docs.
 */
export const buildDefaultDestinationStatus = ({
  loopsStatus = DESTINATION_SYNC_STATUSES.PENDING,
  mixpanelStatus = DESTINATION_SYNC_STATUSES.DISABLED,
} = {}) => ({
  [LIFECYCLE_DESTINATIONS.LOOPS]: {
    status: loopsStatus,
    syncedAt: null,
    lastError: null,
  },
  [LIFECYCLE_DESTINATIONS.MIXPANEL]: {
    status: mixpanelStatus,
    syncedAt: null,
    lastError: null,
  },
});

/**
 * Builds the canonical v1 onboarding object for:
 *
 * accounts/{uid}.onboarding
 *
 * This helper is pure and frontend-safe. It accepts an optional timestampFactory
 * so callers can pass serverTimestamp later if desired.
 */
export const buildInitialOnboardingState = ({
  signupType = SIGNUP_TYPES.SELF_SERVE_TRIAL,
  signupIntent = null,
  signupSource = 'unknown',

  templateKey = null,
  templateEntry = null,
  duplicatedFormId = null,

  subscriptionStatus = 'trialing',
  trialStartedAt = null,
  trialEndsAt = null,
  planKey = null,

  conciergeCandidate = null,
  conciergeStatus = CONCIERGE_STATUSES.NOT_OFFERED,

  timestampFactory = null,
  registry = TEMPLATE_REGISTRY,
  sourceRegistry = SIGNUP_SOURCE_REGISTRY,
} = {}) => {
  const now = makeTimestamp(timestampFactory);

  const sourceMetadata = getSignupSourceMetadata(
    signupSource,
    sourceRegistry
  );

  const normalizedSignupType = normalizeSignupType(signupType);
  const normalizedSignupSource = cleanString(signupSource, 'unknown');

  const hasTemplateKey = isNonEmptyString(templateKey);

  const defaultIntentFromSource =
    sourceMetadata.defaultSignupIntent || SIGNUP_INTENTS.GENERAL_SIGNUP;

  const normalizedSignupIntent = normalizeSignupIntent(
    signupIntent ||
      (hasTemplateKey
        ? SIGNUP_INTENTS.DUPLICATE_TEMPLATE
        : defaultIntentFromSource)
  );

  const templateMetadata = resolveTemplateMetadata({
    templateKey,
    templateEntry,
    registry,
  });

  const isTemplateSignup =
    normalizedSignupIntent === SIGNUP_INTENTS.DUPLICATE_TEMPLATE &&
    hasTemplateKey;

  const templateWasOpened =
    isTemplateSignup && isNonEmptyString(duplicatedFormId);

  const likelyIntent = deriveLikelyIntent({
    templateKey,
    templateEntry,
    signupSource: normalizedSignupSource,
    registry,
    sourceRegistry,
  });

  const userState = isTemplateSignup
    ? templateMetadata.defaultUserState
    : USER_STATES.GENERAL_WEBSITE_SIGNUP;

  const activationStatus = templateWasOpened
    ? templateMetadata.defaultActivationStatus
    : ACTIVATION_STATUSES.NOT_STARTED;

  const lifecycleStage = templateWasOpened
    ? LIFECYCLE_STAGES.TEMPLATE_OPENED
    : LIFECYCLE_STAGES.TRIAL_STARTED;

  const shouldMarkConciergeCandidate =
    typeof conciergeCandidate === 'boolean'
      ? conciergeCandidate
      : Boolean(isTemplateSignup && templateMetadata.conciergeCandidateDefault);

  return {
    schemaVersion: ONBOARDING_SCHEMA_VERSION,

    signupType: normalizedSignupType,
    signupIntent: normalizedSignupIntent,
    signupSource: normalizedSignupSource,
    signupSourceType: sourceMetadata.sourceType,
    signupSourceLabel: sourceMetadata.label,
    likelyIntent,

    userState,
    activationStatus,
    lifecycleStage,

    templateKey: isTemplateSignup ? templateMetadata.templateKey : null,
    templateLabel: isTemplateSignup ? templateMetadata.templateLabel : null,
    templateFamily: isTemplateSignup ? templateMetadata.templateFamily : null,
    templateOutputMode: isTemplateSignup
      ? templateMetadata.templateOutputMode
      : null,
    templateUseCase: isTemplateSignup ? templateMetadata.templateUseCase : null,
    sourcePublishedFormId: isTemplateSignup
      ? templateMetadata.sourcePublishedFormId
      : null,
    duplicatedFormId: templateWasOpened ? duplicatedFormId : null,

    conciergeCandidate: shouldMarkConciergeCandidate,
    conciergeStatus: normalizeConciergeStatus(conciergeStatus),
    lastManualTouchDate: null,

    trialStartedAt: trialStartedAt || now,
    trialEndsAt: trialEndsAt || null,
    subscriptionStatus: subscriptionStatus || 'trialing',
    planKey: planKey || null,

    templateOpenedAt: templateWasOpened ? now : null,
    firstSubmissionAt: null,
    firstDraftSubmissionAt: null,
    firstPublishedSubmissionAt: null,
    firstSavedAt: null,
    firstPublishedAt: null,
    activatedAt: null,
    lastOnboardingEventAt: now,

    lastLoopsSyncAt: null,
    lastLoopsEventName: null,
    lastLoopsSyncStatus: null,

    createdAt: now,
    updatedAt: now,
  };
};

/**
 * Reduces onboarding state from a canonical onboarding event.
 *
 * This is intentionally vendor-neutral:
 * - Firestore can use it as the source-of-truth reducer.
 * - Loops can receive the resulting state as contact properties.
 * - Mixpanel can later receive the same event as analytics.
 */
export const reduceOnboardingStateFromEvent = ({
  currentOnboarding = {},
  eventName,
  eventProperties = {},
  timestampFactory = null,
  registry = TEMPLATE_REGISTRY,
  sourceRegistry = SIGNUP_SOURCE_REGISTRY,
} = {}) => {
  assertValidOnboardingEventName(eventName);

  const now = makeTimestamp(timestampFactory);

  let next = {
    schemaVersion: ONBOARDING_SCHEMA_VERSION,
    ...currentOnboarding,
    updatedAt: now,
    lastOnboardingEventAt: now,
  };

  const currentActivationStatus = normalizeActivationStatus(
    currentOnboarding?.activationStatus
  );

  const currentLifecycleStage = normalizeLifecycleStage(
    currentOnboarding?.lifecycleStage
  );

  switch (eventName) {
    case ONBOARDING_EVENTS.TRIAL_STARTED: {
      const initialState = buildInitialOnboardingState({
        signupType: eventProperties.signupType,
        signupIntent: eventProperties.signupIntent,
        signupSource: eventProperties.signupSource,
        templateKey: eventProperties.templateKey,
        templateEntry: eventProperties.templateEntry,
        duplicatedFormId: eventProperties.duplicatedFormId,
        subscriptionStatus: eventProperties.subscriptionStatus,
        trialStartedAt: eventProperties.trialStartedAt || now,
        trialEndsAt: eventProperties.trialEndsAt,
        planKey: eventProperties.planKey,
        conciergeCandidate: eventProperties.conciergeCandidate,
        conciergeStatus: eventProperties.conciergeStatus,
        timestampFactory: () => now,
        registry,
        sourceRegistry,
      });

      if (hasExistingOnboardingState(currentOnboarding)) {
        return mergeTrialStartedIntoExistingState({
          currentOnboarding,
          initialState,
          now,
        });
      }

      return {
        ...currentOnboarding,
        ...initialState,
        createdAt: currentOnboarding?.createdAt || initialState.createdAt,
        updatedAt: now,
        lastOnboardingEventAt: now,
      };
    }

    case ONBOARDING_EVENTS.TEMPLATE_OPENED: {
      const templateMetadata = resolveTemplateMetadata({
        templateKey: eventProperties.templateKey,
        templateEntry: eventProperties.templateEntry,
        registry,
      });

      const sourceMetadata = getSignupSourceMetadata(
        eventProperties.signupSource || next.signupSource,
        sourceRegistry
      );

      next = {
        ...next,
        signupIntent: SIGNUP_INTENTS.DUPLICATE_TEMPLATE,
        signupSource: cleanString(
          eventProperties.signupSource,
          next.signupSource || 'unknown'
        ),
        signupSourceType: sourceMetadata.sourceType,
        signupSourceLabel: sourceMetadata.label,
        likelyIntent: deriveLikelyIntent({
          templateKey: templateMetadata.templateKey,
          templateEntry: eventProperties.templateEntry,
          signupSource: eventProperties.signupSource || next.signupSource,
          fallback: next.likelyIntent || LIKELY_INTENTS.UNKNOWN,
          registry,
          sourceRegistry,
        }),
        userState: templateMetadata.defaultUserState,
        activationStatus: preserveActivatedStatus(
          currentActivationStatus,
          templateMetadata.defaultActivationStatus
        ),
        lifecycleStage: preserveActivatedLifecycleStage(
          currentActivationStatus,
          currentLifecycleStage,
          LIFECYCLE_STAGES.TEMPLATE_OPENED
        ),
        templateKey: templateMetadata.templateKey,
        templateLabel: templateMetadata.templateLabel,
        templateFamily: templateMetadata.templateFamily,
        templateOutputMode: templateMetadata.templateOutputMode,
        templateUseCase: templateMetadata.templateUseCase,
        sourcePublishedFormId: templateMetadata.sourcePublishedFormId,
        duplicatedFormId:
          eventProperties.duplicatedFormId || next.duplicatedFormId || null,
        conciergeCandidate:
          typeof eventProperties.conciergeCandidate === 'boolean'
            ? eventProperties.conciergeCandidate
            : Boolean(
                next.conciergeCandidate ||
                  templateMetadata.conciergeCandidateDefault
              ),
      };

      next = setFirstTimestamp(next, 'templateOpenedAt', now);

      return next;
    }

    case ONBOARDING_EVENTS.CAMPAIGN_URL_SUBMISSION_CREATED: {
      const submissionMode = normalizeSubmissionMode(
        eventProperties.submissionMode ||
          eventProperties?.metadata?.submissionMode
      );

      next = {
        ...next,
        activationStatus: ACTIVATION_STATUSES.ACTIVATED,
        lifecycleStage: LIFECYCLE_STAGES.ACTIVATED,
      };

      next = setFirstTimestamp(next, 'firstSubmissionAt', now);
      next = setFirstTimestamp(next, 'activatedAt', now);

      if (submissionMode === SUBMISSION_MODES.DRAFT_OR_TEST) {
        next = setFirstTimestamp(next, 'firstDraftSubmissionAt', now);
      }

      if (submissionMode === SUBMISSION_MODES.PUBLISHED) {
        next = setFirstTimestamp(next, 'firstPublishedSubmissionAt', now);
      }

      return next;
    }

    case ONBOARDING_EVENTS.CAMPAIGN_URL_FORM_SAVED: {
      next = setFirstTimestamp(next, 'firstSavedAt', now);
      return next;
    }

    case ONBOARDING_EVENTS.CAMPAIGN_URL_FORM_PUBLISHED: {
      next = {
        ...next,
        lifecycleStage: LIFECYCLE_STAGES.CONVERSION_READY,
      };

      next = setFirstTimestamp(next, 'firstPublishedAt', now);

      return next;
    }

    case ONBOARDING_EVENTS.TRIAL_ENDING_SOON: {
      return next;
    }

    case ONBOARDING_EVENTS.CONCIERGE_CANDIDATE_MARKED: {
      return {
        ...next,
        conciergeCandidate: true,
      };
    }

    case ONBOARDING_EVENTS.CONCIERGE_STATUS_UPDATED: {
      return {
        ...next,
        conciergeCandidate:
          eventProperties.conciergeCandidate === false
            ? false
            : Boolean(next.conciergeCandidate || true),
        conciergeStatus: normalizeConciergeStatus(
          eventProperties.conciergeStatus
        ),
        lastManualTouchDate:
          eventProperties.lastManualTouchDate || now,
      };
    }

    case ONBOARDING_EVENTS.DORMANT_TRIAL_DETECTED: {
      if (currentActivationStatus === ACTIVATION_STATUSES.ACTIVATED) {
        return next;
      }

      return {
        ...next,
        userState: USER_STATES.DORMANT_TRIAL_USER,
        lifecycleStage: LIFECYCLE_STAGES.DORMANT_TRIAL,
      };
    }

    default:
      return next;
  }
};

/**
 * Generates a stable client-side event id for callable payloads.
 *
 * Backend functions should still validate idempotency before writing events.
 */
export const createClientEventId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return [
    'evt',
    Date.now(),
    Math.random().toString(36).slice(2, 12),
  ].join('_');
};

/**
 * Builds a minimal event payload for the future recordOnboardingEvent callable.
 *
 * This is safe for frontend use.
 * Backend should enrich with:
 * - authenticated user id
 * - account document
 * - organization document
 * - before/after lifecycle state
 * - destination sync status
 */
export const buildOnboardingEventPayload = ({
  eventName,
  eventId = null,
  insertId = null,
  formId = null,
  submissionId = null,
  templateKey = null,
  eventSource = ONBOARDING_EVENT_SOURCES.UNKNOWN,
  eventProperties = {},
  metadata = {},
} = {}) => {
  assertValidOnboardingEventName(eventName);

  const resolvedEventId = eventId || createClientEventId();

  return {
    schemaVersion: ONBOARDING_SCHEMA_VERSION,
    eventId: resolvedEventId,
    insertId: insertId || resolvedEventId,
    eventName,
    formId: formId || null,
    submissionId: submissionId || null,
    templateKey: templateKey || null,
    eventSource: eventSource || ONBOARDING_EVENT_SOURCES.UNKNOWN,
    eventProperties: eventProperties || {},
    metadata: metadata || {},
  };
};

/**
 * Builds a canonical event audit document shape.
 *
 * This is useful for backend parity and frontend tests, but the actual event
 * document write should happen server-side in the future recordOnboardingEvent
 * callable.
 */
export const buildOnboardingEventDocument = ({
  eventName,
  eventId = null,
  insertId = null,

  userId = null,
  organizationId = null,
  organizationName = null,

  userStateBefore = null,
  userStateAfter = null,
  activationStatusBefore = null,
  activationStatusAfter = null,
  lifecycleStageBefore = null,
  lifecycleStageAfter = null,

  signupType = null,
  signupIntent = null,
  signupSource = null,
  signupSourceType = null,
  likelyIntent = null,

  templateKey = null,
  templateLabel = null,
  templateFamily = null,
  templateOutputMode = null,
  templateUseCase = null,
  sourcePublishedFormId = null,
  duplicatedFormId = null,

  formId = null,
  submissionId = null,

  eventSource = ONBOARDING_EVENT_SOURCES.UNKNOWN,
  destinationStatus = null,
  eventProperties = {},
  metadata = {},

  timestampFactory = null,
} = {}) => {
  assertValidOnboardingEventName(eventName);

  const now = makeTimestamp(timestampFactory);
  const resolvedEventId = eventId || createClientEventId();

  return {
    schemaVersion: ONBOARDING_SCHEMA_VERSION,

    eventId: resolvedEventId,
    insertId: insertId || resolvedEventId,
    eventName,

    userId: userId || null,
    organizationId: organizationId || null,
    organizationName: organizationName || null,

    signupType: signupType || null,
    signupIntent: signupIntent || null,
    signupSource: signupSource || null,
    signupSourceType: signupSourceType || null,
    likelyIntent: likelyIntent || LIKELY_INTENTS.UNKNOWN,

    userStateBefore: userStateBefore || null,
    userStateAfter: userStateAfter || null,
    activationStatusBefore: activationStatusBefore || null,
    activationStatusAfter: activationStatusAfter || null,
    lifecycleStageBefore: lifecycleStageBefore || null,
    lifecycleStageAfter: lifecycleStageAfter || null,

    templateKey: templateKey || null,
    templateLabel: templateLabel || null,
    templateFamily: templateFamily || null,
    templateOutputMode: templateOutputMode || null,
    templateUseCase: templateUseCase || null,
    sourcePublishedFormId: sourcePublishedFormId || null,
    duplicatedFormId: duplicatedFormId || null,

    formId: formId || null,
    submissionId: submissionId || null,

    eventSource: eventSource || ONBOARDING_EVENT_SOURCES.UNKNOWN,
    destinationStatus:
      destinationStatus || buildDefaultDestinationStatus(),

    eventProperties: eventProperties || {},
    metadata: metadata || {},

    createdAt: now,
  };
};

/**
 * Returns true when a contact should enter the self-serve trial onboarding path.
 *
 * Invited users are intentionally excluded from the v1 self-serve activation loop.
 */
export const shouldEnrollInSelfServeTrialOnboarding = (onboarding = {}) => {
  return onboarding.signupType === SIGNUP_TYPES.SELF_SERVE_TRIAL;
};

/**
 * Returns true when a user is considered activated for v1 onboarding.
 */
export const isActivated = (onboarding = {}) => {
  return onboarding.activationStatus === ACTIVATION_STATUSES.ACTIVATED;
};

/**
 * Returns true when a user has reached the editable template state.
 */
export const hasOpenedTemplate = (onboarding = {}) => {
  return onboarding.activationStatus === ACTIVATION_STATUSES.TEMPLATE_OPENED ||
    onboarding.templateOpenedAt ||
    onboarding.duplicatedFormId;
};

/**
 * Returns true when a user should be visible in a manual concierge review.
 */
export const isConciergeCandidate = (onboarding = {}) => {
  return onboarding.conciergeCandidate === true;
};

/**
 * Builds safe lifecycle properties for Loops or future analytics destinations.
 *
 * This intentionally excludes:
 * - stripeCustomerId
 * - subscriptionId
 * - stripeSubscriptionItemId
 * - payment details
 * - invite tokens
 * - password data
 * - full permissions arrays
 * - raw pending billing objects
 */
export const buildLifecycleContactProperties = ({
  account = {},
  organization = {},
} = {}) => {
  const onboarding = account?.onboarding || {};

  return {
    userId: account?.uid || account?.id || null,
    email: account?.email || null,
    firstName: account?.firstName || '',
    lastName: account?.lastName || '',

    organizationId:
      account?.organizationId || organization?.id || null,
    organizationName:
      organization?.name || null,

    signupType: onboarding.signupType || null,
    signupIntent: onboarding.signupIntent || null,
    signupSource: onboarding.signupSource || null,
    signupSourceType:
      onboarding.signupSourceType || SIGNUP_SOURCE_TYPES.UNKNOWN,
    signupSourceLabel: onboarding.signupSourceLabel || null,
    likelyIntent: onboarding.likelyIntent || LIKELY_INTENTS.UNKNOWN,

    userState: onboarding.userState || null,
    activationStatus:
      onboarding.activationStatus || ACTIVATION_STATUSES.NOT_STARTED,
    lifecycleStage:
      onboarding.lifecycleStage || LIFECYCLE_STAGES.TRIAL_STARTED,

    templateKey: onboarding.templateKey || null,
    templateLabel: onboarding.templateLabel || null,
    templateFamily: onboarding.templateFamily || null,
    templateOutputMode: onboarding.templateOutputMode || null,
    templateUseCase: onboarding.templateUseCase || null,
    duplicatedFormId: onboarding.duplicatedFormId || null,

    subscriptionStatus:
      onboarding.subscriptionStatus ||
      organization?.subscriptionStatus ||
      account?.subscriptionStatus ||
      null,
    trialEndsAt:
      onboarding.trialEndsAt ||
      organization?.trialEndsAt ||
      account?.trialEndsAt ||
      null,
    planKey:
      onboarding.planKey ||
      organization?.planKey ||
      account?.planKey ||
      null,

    conciergeCandidate: Boolean(onboarding.conciergeCandidate),
    conciergeStatus:
      onboarding.conciergeStatus || CONCIERGE_STATUSES.NOT_OFFERED,
  };
};

/**
 * Builds event properties suitable for a future Mixpanel adapter.
 *
 * This does not import or call Mixpanel.
 */
export const buildAnalyticsEventProperties = ({
  uid = null,
  eventName,
  eventId = null,
  insertId = null,
  onboarding = {},
  organization = {},
  formId = null,
  submissionId = null,
  metadata = {},
} = {}) => {
  assertValidOnboardingEventName(eventName);

  const resolvedEventId = eventId || createClientEventId();

  return {
    eventName,
    distinct_id: uid || null,
    $insert_id: insertId || resolvedEventId,

    userId: uid || null,
    organizationId:
      onboarding.organizationId || organization?.id || null,
    organizationName:
      onboarding.organizationName || organization?.name || null,

    signupType: onboarding.signupType || null,
    signupIntent: onboarding.signupIntent || null,
    signupSource: onboarding.signupSource || null,
    signupSourceType: onboarding.signupSourceType || null,
    likelyIntent: onboarding.likelyIntent || LIKELY_INTENTS.UNKNOWN,

    userState: onboarding.userState || null,
    activationStatus:
      onboarding.activationStatus || ACTIVATION_STATUSES.NOT_STARTED,
    lifecycleStage:
      onboarding.lifecycleStage || LIFECYCLE_STAGES.TRIAL_STARTED,

    templateKey: onboarding.templateKey || null,
    templateLabel: onboarding.templateLabel || null,
    templateFamily: onboarding.templateFamily || null,
    templateOutputMode: onboarding.templateOutputMode || null,
    templateUseCase: onboarding.templateUseCase || null,

    formId: formId || null,
    submissionId: submissionId || null,

    metadata: metadata || {},
  };
};

/**
 * Builds the top-level custom properties expected by Loops contact/event sync.
 *
 * This does not import or call Loops.
 */
export const buildLoopsContactProperties = ({
  account = {},
  organization = {},
} = {}) => {
  return buildLifecycleContactProperties({
    account,
    organization,
  });
};

/**
 * Builds a Loops-compatible event payload shape for a future backend adapter.
 *
 * Important:
 * - Backend should add the Loops API call.
 * - Frontend should not send this directly to Loops.
 * - Loops custom properties are mirrored at the top level.
 */
export const buildLoopsLifecycleEventPayload = ({
  account = {},
  organization = {},
  eventName,
  eventId = null,
  insertId = null,
  formId = null,
  submissionId = null,
  metadata = {},
} = {}) => {
  assertValidOnboardingEventName(eventName);

  const lifecycleProperties = buildLifecycleContactProperties({
    account,
    organization,
  });

  const resolvedEventId = eventId || createClientEventId();

  return {
    email: lifecycleProperties.email,
    userId: lifecycleProperties.userId,
    eventName,

    eventProperties: {
      eventId: resolvedEventId,
      insertId: insertId || resolvedEventId,
      organizationId: lifecycleProperties.organizationId,
      organizationName: lifecycleProperties.organizationName,
      signupType: lifecycleProperties.signupType,
      signupIntent: lifecycleProperties.signupIntent,
      signupSource: lifecycleProperties.signupSource,
      signupSourceType: lifecycleProperties.signupSourceType,
      likelyIntent: lifecycleProperties.likelyIntent,
      userState: lifecycleProperties.userState,
      activationStatus: lifecycleProperties.activationStatus,
      lifecycleStage: lifecycleProperties.lifecycleStage,
      templateKey: lifecycleProperties.templateKey,
      templateLabel: lifecycleProperties.templateLabel,
      templateFamily: lifecycleProperties.templateFamily,
      templateOutputMode: lifecycleProperties.templateOutputMode,
      templateUseCase: lifecycleProperties.templateUseCase,
      formId: formId || null,
      submissionId: submissionId || null,
      metadata: metadata || {},
    },

    ...lifecycleProperties,
  };
};